import type { SeedIsland } from './seedIslands'

/**
 * Mandarin above HSK 3.
 *
 * The library used to stop dead at HSK 3: ten islands there, nothing above it.
 * That was invisible while the level was decoration, and became the whole
 * problem the moment it started deciding what you are shown — a learner who
 * moved past HSK 3 found the setting had nowhere left to go, and the product
 * quietly had nothing more to teach them.
 *
 * The topics escalate the way the method wants rather than the way a syllabus
 * would. At HSK 4 you are explaining your job past the first answer and telling
 * a story with a shape. At 5 the register carries the weight — how you actually
 * feel about your hours, what you think AI will and will not take. At 6 the
 * value is what a native says when they know the question is contested: the
 * hedges, the concession before the point, the 成语 that lands because it is
 * the right one and not because it was on a list.
 *
 * Pinyin is stored as SPOKEN, with 一/不 sandhi applied, one syllable per Han
 * character, matching the rest of the seed data.
 */
export const ADVANCED_ZH: SeedIsland[] = [
  {
    id: 'isl_zhuanye',
    title: 'What I do at work',
    titleZh: '我的工作内容',
    useWhen:
      'When someone asks what you do and then keeps asking — which piece is yours, who you work with, what a normal day looks like, how you ended up in it.',
    tags: ['work', 'career', 'small talk'],
    hskLevel: 4,
    blocks: [
      [
        '我在一家软件公司上班，主要负责数据这块。',
        'wǒ zài yì jiā ruǎn jiàn gōng sī shàng bān zhǔ yào fù zé shù jù zhè kuài',
        'I work at a software company; data is the part I own.',
        '这块 (or 这一块) is how people carve up responsibility at work — "the ___ side of things." Swap in 设计这块, 销售这块.',
      ],
      [
        '我们组有八个人，我跟产品经理联系最多。',
        'wǒ men zǔ yǒu bā ge rén wǒ gēn chǎn pǐn jīng lǐ lián xì zuì duō',
        'There are eight of us on the team, and the person I deal with most is the product manager.',
      ],
      [
        '一般上午开会，下午才能把手上的事做完。',
        'yì bān shàng wǔ kāi huì xià wǔ cái néng bǎ shǒu shàng de shì zuò wán',
        'Mornings are meetings, so I don\'t get through what\'s on my plate until the afternoon.',
      ],
      [
        '无论出了什么问题，最后都得我来解决。',
        'wú lùn chū le shén me wèn tí zuì hòu dōu děi wǒ lái jiě jué',
        'Whatever goes wrong, it lands on me in the end.',
        '得 here is děi, "have to" — same character as the particle de, completely different word. 都得我来 is the standard way to claim (or complain about) ownership.',
      ],
      [
        '我不是学这个专业的，都是工作以后学会的。',
        'wǒ bú shì xué zhè ge zhuān yè de dōu shì gōng zuò yǐ hòu xué huì de',
        'I didn\'t study this — I picked it all up on the job.',
      ],
      [
        '虽然经常加班，但我对这个方向特别感兴趣。',
        'suī rán jīng cháng jiā bān dàn wǒ duì zhè ge fāng xiàng tè bié gǎn xìng qù',
        'The hours are long, but this is a field I\'m genuinely interested in.',
      ],
    ],
  },
  {
    id: 'isl_bijiao',
    title: 'Here versus home',
    titleZh: '两边的生活',
    useWhen:
      'When someone finds out where you are from and asks how life here compares — which happens in the first five minutes of nearly every conversation you will have.',
    tags: ['culture', 'living abroad', 'small talk'],
    hskLevel: 4,
    blocks: [
      [
        '对我来说，最大的区别是这边什么都快得多。',
        'duì wǒ lái shuō zuì dà de qū bié shì zhè biān shén me dōu kuài de duō',
        'For me the biggest difference is that everything here moves much faster.',
      ],
      [
        '最让我吃惊的是，出门只带手机就够了。',
        'zuì ràng wǒ chī jīng de shì chū mén zhǐ dài shǒu jī jiù gòu le',
        'What floored me is that you can walk out the door with nothing but your phone.',
      ],
      [
        '我现在已经习惯了，回国才觉得不方便。',
        'wǒ xiàn zài yǐ jīng xí guàn le huí guó cái jué de bù fāng biàn',
        'I\'m used to it now — it\'s going home that feels inconvenient.',
        '才 is what makes this the joke your listener is waiting for: it pins the inconvenience on the trip home. Drop it and the line turns into a complaint about your own country.',
      ],
      [
        '这边的人一见面就问工资，我还是有点不习惯。',
        'zhè biān de rén yí jiàn miàn jiù wèn gōng zī wǒ hái shì yǒu diǎn bù xí guàn',
        'People asking what I earn the minute they meet me — that one still gets me.',
        'Asking about pay isn\'t rude here, it\'s small talk. 有点不习惯 lets you name it as your problem rather than their bad manners, which is the whole reason this line is safe to say.',
      ],
      [
        '虽然有时候想家，但是我现在还不想回去。',
        'suī rán yǒu shí hou xiǎng jiā dàn shì wǒ xiàn zài hái bù xiǎng huí qù',
        'I do get homesick, but I\'m in no hurry to go back.',
      ],
      [
        '其实很难说哪边更好，两边各有各的好处。',
        'qí shí hěn nán shuō nǎ biān gèng hǎo liǎng biān gè yǒu gè de hǎo chu',
        'Honestly I couldn\'t say which is better — both have their good points.',
      ],
    ],
  },
  {
    id: 'isl_jingli',
    title: 'A story that went wrong',
    titleZh: '讲一件麻烦事',
    useWhen:
      'When someone asks how your week or your trip went and you want to tell the whole small disaster — setup, the moment it went wrong, and how it ended — instead of answering in one sentence.',
    tags: ['storytelling', 'travel', 'mishaps'],
    hskLevel: 4,
    blocks: [
      [
        '上个星期我遇到了一件特别麻烦的事。',
        'shàng ge xīng qī wǒ yù dào le yí jiàn tè bié má fan de shì',
        'So last week this really annoying thing happened to me.',
        '遇到了一件…的事 is the standard opener — it signals a story is coming and buys you a second to organize the rest of it.',
      ],
      [
        '那天我赶着去机场，一上车就一直在看手机。',
        'nà tiān wǒ gǎn zhe qù jī chǎng yí shàng chē jiù yì zhí zài kàn shǒu jī',
        'I was rushing to the airport, and the second I got in the car I had my head in my phone.',
      ],
      [
        '车开走以后，我才发现把包忘在车上了。',
        'chē kāi zǒu yǐ hòu wǒ cái fā xiàn bǎ bāo wàng zài chē shàng le',
        'It wasn\'t until the car had pulled away that I realized I\'d left my bag in it.',
        '才 is doing the work here — it says the realization came later than it should have. Drop it and the line loses the whole point.',
      ],
      [
        '包里不但有护照，还有电脑，我当时急死了。',
        'bāo lǐ bú dàn yǒu hù zhào hái yǒu diàn nǎo wǒ dāng shí jí sǐ le',
        'The bag had my passport in it, and my laptop on top of that — I was freaking out.',
      ],
      [
        '还好联系上了司机，他又把包给我送回来了。',
        'hái hǎo lián xì shàng le sī jī tā yòu bǎ bāo gěi wǒ sòng huí lái le',
        'Luckily I managed to get hold of the driver, and he ran the bag back to me.',
        '联系上 — the 上 says you actually reached him. 联系了 only says you tried.',
      ],
      [
        '包虽然没丢，但是我下车前一定要检查一下。',
        'bāo suī rán méi diū dàn shì wǒ xià chē qián yí dìng yào jiǎn chá yí xià',
        'I didn\'t end up losing the bag, but now I always check before I get out of a car.',
      ],
    ],
  },
  {
    id: 'isl_butongyi',
    title: 'Disagreeing politely',
    titleZh: '提出不同意见',
    useWhen:
      'When you\'re in a meeting or discussion and need to push back on someone\'s plan — softening it, giving your reason, and staying on record — without turning it into a fight.',
    tags: ['disagreement', 'work', 'opinions'],
    hskLevel: 4,
    blocks: [
      [
        '我明白你的意思，不过我的看法有点不一样。',
        'wǒ míng bai nǐ de yì si bú guò wǒ de kàn fǎ yǒu diǎn bù yí yàng',
        'I see what you\'re getting at, but I look at it a bit differently.',
        '不过 is a lighter "but" than 但是 — it keeps the disagreement small. Saying 我明白你的意思 first is what makes the rest land as a discussion instead of a contradiction.',
      ],
      [
        '我觉得这个办法太危险了，时间也不够。',
        'wǒ jué de zhè ge bàn fǎ tài wēi xiǎn le shí jiān yě bú gòu',
        'I think that approach is too risky, and we don\'t have the time for it either.',
      ],
      [
        '你说的这几点都对，但我还是不太同意。',
        'nǐ shuō de zhè jǐ diǎn dōu duì dàn wǒ hái shì bú tài tóng yì',
        'Everything you just said is fair — I still don\'t quite agree.',
        '不太同意 is a real disagreement worded as a small one; 我不同意 on its own comes out flat and hard.',
      ],
      [
        '虽然大家都着急，但这件事不能这么快决定。',
        'suī rán dà jiā dōu zháo jí dàn zhè jiàn shì bù néng zhè me kuài jué dìng',
        'I know everyone\'s in a hurry, but this isn\'t a decision we should make this fast.',
      ],
      [
        '要不这样，先试一个月，不行的话再改回来。',
        'yào bú zhè yàng xiān shì yí ge yuè bù xíng de huà zài gǎi huí lai',
        'How about this — we try it for a month, and if it doesn\'t work we change it back.',
        '要不这样 floats a proposal you\'re not insisting on; it\'s the standard way to open a compromise. 改回来 is what makes the offer safe enough to accept.',
      ],
      [
        '无论最后怎么定，我都想把我的意见说清楚。',
        'wú lùn zuì hòu zěn me dìng wǒ dōu xiǎng bǎ wǒ de yì jiàn shuō qīng chu',
        'However it gets decided in the end, I want my position on the record.',
        'The line for when you\'ve lost the decision but not the argument. 把…说清楚 puts the weight on being heard rather than on winning.',
      ],
    ],
  },
  {
    id: 'isl_yali',
    title: 'Burned out at work',
    titleZh: '工作压力太大了',
    useWhen:
      'When someone asks how work is going and you want to say what it actually costs you, instead of stopping at 很忙、还行。',
    tags: ['work', 'burnout', 'opinions'],
    hskLevel: 5,
    blocks: [
      [
        '说实话，最近工作压力挺大的，几乎每天都要加班。',
        'shuō shí huà zuì jìn gōng zuò yā lì tǐng dà de jī hū měi tiān dōu yào jiā bān',
        'Honestly, work has been pretty rough lately — I\'m staying late almost every day.',
      ],
      [
        '其实我不怕忙，最累的是那种停不下来的感觉。',
        'qí shí wǒ bú pà máng zuì lèi de shì nà zhǒng tíng bú xià lái de gǎn jué',
        'It\'s not the workload that gets me — it\'s never being able to switch off.',
        '其实 pushes back on what the other person has already assumed (that you\'re complaining about hours). It buys you the right to disagree without sounding like you\'re arguing.',
      ],
      [
        '下了班手机还是响个不停，领导发消息我不敢不回。',
        'xià le bān shǒu jī hái shì xiǎng ge bù tíng lǐng dǎo fā xiāo xi wǒ bù gǎn bù huí',
        'Even off the clock my phone won\'t stop — if my boss messages, I can\'t just leave it.',
        '不敢不回 is a double negative: not "I reply" but "I don\'t dare not to." It puts the pressure outside you, which is exactly the point you\'re making.',
      ],
      [
        '周末我尽量不看工作群，不然休息了反而更焦虑。',
        'zhōu mò wǒ jǐn liàng bú kàn gōng zuò qún bù rán xiū xi le fǎn ér gèng jiāo lǜ',
        'I try to stay out of the work chat on weekends, or the time off just makes me more anxious.',
      ],
      [
        '多挣的那点钱，未必比得上熬夜熬坏的身体。',
        'duō zhèng de nà diǎn qián wèi bì bǐ de shàng áo yè áo huài de shēn tǐ',
        'The extra money isn\'t necessarily worth what staying up for it does to your body.',
      ],
      [
        '我倒是想过换个轻松点的工作，就是哪里都差不多。',
        'wǒ dào shì xiǎng guo huàn ge qīng sōng diǎn de gōng zuò jiù shì nǎ lǐ dōu chà bù duō',
        'I have thought about moving somewhere easier, but it\'s the same everywhere.',
        '倒是……就是…… is the concession-then-catch pair: you grant the obvious suggestion before the listener can offer it, then name the one thing that kills it. 可惜 would be for something you lost, not for a fact that was always true.',
      ],
    ],
  },
  {
    id: 'isl_keji',
    title: 'AI and my line of work',
    titleZh: '人工智能与我这一行',
    useWhen:
      'When someone asks whether AI is coming for your job and you want to give a considered answer instead of a shrug.',
    tags: ['work', 'opinions', 'technology'],
    hskLevel: 5,
    blocks: [
      [
        '说实话，我这行受到的冲击比大家想象的要小。',
        'shuō shí huà wǒ zhè háng shòu dào de chōng jī bǐ dà jiā xiǎng xiàng de yào xiǎo',
        'Honestly, my field has taken less of a hit than people assume.',
        '这行 is háng, not xíng — "this line of work." Reading it xíng is the giveaway.',
      ],
      [
        '重复性强的工作，现在基本上都交给机器做了。',
        'chóng fù xìng qiáng de gōng zuò xiàn zài jī běn shàng dōu jiāo gěi jī qì zuò le',
        'The repetitive end of the job all gets handed off to machines now.',
      ],
      [
        '真正需要判断和担责任的部分，机器还代替不了。',
        'zhēn zhèng xū yào pàn duàn hé dān zé rèn de bù fen jī qì hái dài tì bù liǎo',
        'The parts that take real judgment, and someone to carry the blame, machines still can\'t take over.',
        '…不了 is the "not capable of it" negative. Flatter and more final than 不能代替, which sounds like a rule rather than a fact.',
      ],
      [
        '有意思的是，工具越强，对人的要求反而越高。',
        'yǒu yì si de shì gōng jù yuè qiáng duì rén de yāo qiú fǎn ér yuè gāo',
        'The funny part is, the better the tools get, the more is demanded of the person.',
      ],
      [
        '客户要的其实是一个能负责的人，不只是一份报告。',
        'kè hù yào de qí shí shì yí gè néng fù zé de rén bù zhǐ shì yí fèn bào gào',
        'What the client actually wants is a person who\'ll stand behind it, not just a document.',
      ],
      [
        '与其担心被取代，不如想想哪些事只有人能做。',
        'yǔ qí dān xīn bèi qǔ dài bù rú xiǎng xiǎng nǎ xiē shì zhǐ yǒu rén néng zuò',
        'Rather than worry about being replaced, I\'d think about what only people can do.',
        '与其…不如 lets you refuse someone\'s framing without arguing with it. Reusable in any debate.',
      ],
    ],
  },
  {
    id: 'isl_jiaoyu',
    title: 'Raising kids',
    titleZh: '教育孩子',
    useWhen:
      'When a Chinese parent or colleague asks how you raise your kid or what you make of the schooling here — usually within minutes of learning you have children.',
    tags: ['parenting', 'education', 'opinions'],
    hskLevel: 5,
    blocks: [
      [
        '我家孩子成绩一般，动手能力倒是特别强。',
        'wǒ jiā hái zi chéng jì yì bān dòng shǒu néng lì dào shì tè bié qiáng',
        'My kid\'s grades are nothing special — he\'s really good with his hands, though.',
      ],
      [
        '说实话，我不太在乎分数，更在乎他有没有自己的想法。',
        'shuō shí huà wǒ bú tài zài hu fēn shù gèng zài hu tā yǒu méi yǒu zì jǐ de xiǎng fǎ',
        'Honestly, I don\'t care much about grades — what I care about is whether he thinks for himself.',
      ],
      [
        '现在孩子周末还要上补习班，我觉得反而会失去兴趣。',
        'xiàn zài hái zi zhōu mò hái yào shàng bǔ xí bān wǒ jué de fǎn ér huì shī qù xìng qù',
        'Kids these days have cram school on weekends on top of it all — I think it just makes them lose interest.',
        '反而 marks a result that goes against what was intended. The whole argument sits in that one word; drop it and the line becomes a bare complaint.',
      ],
      [
        '我一直让他自己做决定，就算做错了，也是一种经验。',
        'wǒ yì zhí ràng tā zì jǐ zuò jué dìng jiù suàn zuò cuò le yě shì yì zhǒng jīng yàn',
        'I\'ve always let him make his own calls; even when he gets it wrong, that counts as experience.',
      ],
      [
        '当然，我也理解这里竞争太激烈，家长其实也是被逼的。',
        'dāng rán wǒ yě lǐ jiě zhè lǐ jìng zhēng tài jī liè jiā zhǎng qí shí yě shì bèi bī de',
        'Of course I get it — the competition here is brutal, and parents don\'t really have a choice either.',
      ],
      [
        '美国的教育也未必就好，我们那里的孩子基础差得多。',
        'měi guó de jiào yù yě wèi bì jiù hǎo wǒ men nà lǐ de hái zi jī chǔ chà de duō',
        'American schooling isn\'t necessarily any better — kids back home have much weaker fundamentals.',
        '未必 says what 不一定 says, but in a more educated, written register — it does not soften the doubt, it states it more crisply. The edge is in 就: 未必就好 is "not necessarily any better." The line\'s job is to balance the five before it — after criticizing schooling here, you take the same swing at your own country\'s.',
      ],
    ],
  },
  {
    id: 'isl_shangwu',
    title: 'Speaking in meetings',
    titleZh: '会议发言',
    useWhen:
      'When you have to hold your own side of a work meeting in Chinese — opening it, putting a proposal on the table, and saying no to a date without sounding blunt.',
    tags: ['business', 'meetings', 'formal'],
    hskLevel: 5,
    blocks: [
      [
        '感谢各位抽出时间，我先简单说明一下今天的背景。',
        'gǎn xiè gè wèi chōu chū shí jiān wǒ xiān jiǎn dān shuō míng yí xià jīn tiān de bèi jǐng',
        'Thanks for making the time, everyone. Let me set out the background first.',
      ],
      [
        '我建议先小范围试一下，成本和风险都比较好控制。',
        'wǒ jiàn yì xiān xiǎo fàn wéi shì yí xià chéng běn hé fēng xiǎn dōu bǐ jiào hǎo kòng zhì',
        'What I\'d suggest is trying it small-scale first — that keeps cost and risk manageable.',
      ],
      [
        '这只是我的初步想法，有什么不同意见，随时打断我。',
        'zhè zhǐ shì wǒ de chū bù xiǎng fǎ yǒu shén me bù tóng yì jiàn suí shí dǎ duàn wǒ',
        'This is just my first pass at it — if anyone sees it differently, cut in any time.',
      ],
      [
        '说实话，这个时间太紧了，质量上恐怕很难保证。',
        'shuō shí huà zhè ge shí jiān tài jǐn le zhì liàng shàng kǒng pà hěn nán bǎo zhèng',
        'Honestly, that timeline is too tight — I don\'t think we could hold the quality.',
        '说实话 announces that disagreement is coming so nobody is ambushed; 恐怕 turns the bad news into a worry rather than a verdict. Strip either one and the same sentence lands as a flat refusal.',
      ],
      [
        '如果能往后推一周，我们倒是可以保证质量。',
        'rú guǒ néng wǎng hòu tuī yì zhōu wǒ men dào shì kě yǐ bǎo zhèng zhì liàng',
        'Give us one more week and we can commit to the quality.',
        '倒是 turns back on the refusal you just made — "in that case we actually could" — so the offer reads as reopening the door you closed one line earlier. It needs that refusal in front of it to push against; used cold, with nothing to reverse, it sounds like it is contradicting something the listener never said.',
      ],
      [
        '那今天就先到这里，会后我把记录整理好发给各位。',
        'nà jīn tiān jiù xiān dào zhè lǐ huì hòu wǒ bǎ jì lù zhěng lǐ hǎo fā gěi gè wèi',
        'Let\'s leave it there for today. I\'ll write up the notes afterward and send them out to everyone.',
      ],
    ],
  },
  {
    id: 'isl_shehui',
    title: 'A contested opinion',
    titleZh: '谈敏感话题',
    useWhen:
      'When the conversation turns to something you actually have a view on — school pressure, work culture, how people raise their kids — and you want to state it fully without sounding like you are lecturing someone who lives it.',
    tags: ['opinion', 'hedging', 'society'],
    hskLevel: 6,
    blocks: [
      [
        '我知道这个话题挺敏感的，但说句心里话，我觉得现在孩子的负担确实太重了。',
        'wǒ zhī dào zhè ge huà tí tǐng mǐn gǎn de dàn shuō jù xīn lǐ huà wǒ jué de xiàn zài hái zi de fù dān què shí tài zhòng le',
        'I know this is a touchy subject, but honestly, I think the load on kids these days really is too heavy.',
      ],
      [
        '我也不是不理解家长，谁都不敢先停下来，说白了就是被卷进去了。',
        'wǒ yě bú shì bù lǐ jiě jiā zhǎng shéi dōu bù gǎn xiān tíng xià lai shuō bái le jiù shì bèi juǎn jìn qù le',
        'It\'s not that I don\'t sympathize with the parents — nobody dares be the first one to ease off. They got swept along with everyone else, plain and simple.',
      ],
      [
        '问题不在于努不努力，而在于付出和回报早就不成正比了。',
        'wèn tí bú zài yú nǔ bù nǔ lì ér zài yú fù chū hé huí bào zǎo jiù bù chéng zhèng bǐ le',
        'The question isn\'t whether kids work hard enough; it\'s that effort and reward stopped lining up a long time ago.',
        '不在于…而在于… lets you move the argument instead of contradicting the person — you accept their premise and relocate the problem. Reusable on any contested topic.',
      ],
      [
        '其实我们那边也好不到哪里去，只不过换了一种方式罢了。',
        'qí shí wǒ men nà biān yě hǎo bú dào nǎ lǐ qù zhǐ bú guò huàn le yì zhǒng fāng shì bà le',
        'To be fair, we\'re no better back home — it just shows up in a different form.',
      ],
      [
        '当然，我毕竟是个外人，站着说话不腰疼，真轮到我做父母未必能免俗。',
        'dāng rán wǒ bì jìng shì ge wài rén zhàn zhe shuō huà bù yāo téng zhēn lún dào wǒ zuò fù mǔ wèi bì néng miǎn sú',
        'Of course, I\'m an outsider, so it\'s easy for me to talk — if I were the parent, I doubt I\'d hold out either.',
        '站着说话不腰疼 is normally an accusation aimed at a bystander. Turning it on yourself lands the objection before your listener can, which is what keeps a strong opinion from sounding smug.',
      ],
      [
        '这也就是我个人的一点看法，你从小在中国长大，感受可能完全不一样吧？',
        'zhè yě jiù shì wǒ gè rén de yì diǎn kàn fǎ nǐ cóng xiǎo zài zhōng guó zhǎng dà gǎn shòu kě néng wán quán bù yí yàng ba',
        'That\'s just my own take — you grew up in it, so it probably feels completely different from where you\'re standing.',
        'The 吧 is doing the whole job. Without it you are telling them how they feel; with it you are handing them the floor.',
      ],
    ],
  },
  {
    id: 'isl_wenhua',
    title: 'Behind a cultural difference',
    titleZh: '文化差异的背后',
    useWhen:
      'When a conversation has settled on \\"that\'s just how their culture is\\" and you want to push it toward the actual reasons without sounding like you\'re lecturing the people who live it.',
    tags: ['culture', 'hedging', 'opinions'],
    hskLevel: 6,
    blocks: [
      [
        '我总觉得把这种差异归结为文化不同，其实等于什么都没说。',
        'wǒ zǒng jué de bǎ zhè zhǒng chā yì guī jié wéi wén huà bù tóng qí shí děng yú shén me dōu méi shuō',
        'I always think chalking a difference like this up to "culture" is really just a way of saying nothing.',
      ],
      [
        '与其说是观念不同，不如说是养老靠谁的问题，答案不一样，做法自然也不一样。',
        'yǔ qí shuō shì guān niàn bù tóng bù rú shuō shì yǎng lǎo kào shuí de wèn tí dá àn bù yí yàng zuò fǎ zì rán yě bù yí yàng',
        'It\'s less about values than about who you\'re expected to depend on in old age — different answer there, so of course different behavior.',
        '与其说…不如说 replaces someone\'s explanation without telling them they\'re wrong. It\'s the move that shifts the conversation from character to circumstance.',
      ],
      [
        '在我看来这更像是殊途同归，要解决的是同一个问题，只是路子不一样。',
        'zài wǒ kàn lái zhè gèng xiàng shì shū tú tóng guī yào jiě jué de shì tóng yí gè wèn tí zhǐ shì lù zi bù yí yàng',
        'To me it\'s the same problem being solved two different ways, not two different goals.',
      ],
      [
        '当然，我一个外人说这些，难免有点隔靴搔痒，你们自己的体会肯定比我深。',
        'dāng rán wǒ yí gè wài rén shuō zhè xiē nán miǎn yǒu diǎn gé xuē sāo yǎng nǐ men zì jǐ de tǐ huì kěn dìng bǐ wǒ shēn',
        'Obviously I\'m on the outside of this, so I\'m only ever half getting it — you\'ve lived it, so you feel it in a way I can\'t.',
        '隔靴搔痒 is scratching an itch through your boot: close, but never reaching the thing. Aiming it at yourself is what keeps the previous lines from landing as a lecture.',
      ],
      [
        '而且这种差异也不是一成不变的，我父母那一代跟我这一代，看法就已经差很多了。',
        'ér qiě zhè zhǒng chā yì yě bú shì yì chéng bú biàn de wǒ fù mǔ nà yí dài gēn wǒ zhè yí dài kàn fǎ jiù yǐ jīng chà hěn duō le',
        'And none of it is fixed — my parents\' generation and mine already see it completely differently.',
      ],
      [
        '身在其中的人反而最难看清自己，我也是到了这边才发现自己有多少想当然的地方。',
        'shēn zài qí zhōng de rén fǎn ér zuì nán kàn qīng zì jǐ wǒ yě shì dào le zhè biān cái fā xiàn zì jǐ yǒu duō shao xiǎng dāng rán de dì fang',
        'The people inside something are the last to see it clearly — I only noticed how much I\'d been taking for granted once I got here.',
      ],
    ],
  },
  {
    id: 'isl_chengyu',
    title: 'Using idioms well',
    titleZh: '成语怎么用才自然',
    useWhen:
      'When someone compliments your Chinese or the talk turns to how you sound, and you want to discuss idioms without performing them.',
    tags: ['idioms', 'register', 'conversation'],
    hskLevel: 6,
    blocks: [
      [
        '你别夸我了，成语我背了一堆，一开口就怕用得不伦不类。',
        'nǐ bié kuā wǒ le chéng yǔ wǒ bèi le yì duī yì kāi kǒu jiù pà yòng de bù lún bú lèi',
        'Oh, stop — I\'ve memorized a pile of them, I just don\'t trust myself to get one out without it landing wrong.',
      ],
      [
        '成语这东西，用对了是锦上添花，用不好就是弄巧成拙。',
        'chéng yǔ zhè dōng xi yòng duì le shì jǐn shàng tiān huā yòng bù hǎo jiù shì nòng qiǎo chéng zhuō',
        'That\'s the thing with idioms — the right one lifts the whole sentence, the wrong one just makes it worse.',
      ],
      [
        '成语堆得太多，反倒像在念稿子。像开夜车这种说法，听着才自然。',
        'chéng yǔ duī de tài duō fǎn dào xiàng zài niàn gǎo zi xiàng kāi yè chē zhè zhǒng shuō fǎ tīng zhe cái zì rán',
        'Stack too many up and you sound like you\'re reading off a script. Something like 开夜车 is what a person actually says.',
        '开夜车 is a 惯用语, not a 成语: it is verb-object, so it splits and takes aspect like a real verb — 昨晚开了个夜车. A 成语 is a frozen four-character block that cannot. That difference is most of why one sounds spoken and the other sounds written.',
      ],
      [
        '真正敢用的就这么几个，都是跟同事聊天听来的，什么场合能用，心里大概有数。',
        'zhēn zhèng gǎn yòng de jiù zhè me jǐ ge dōu shì gēn tóng shì liáo tiān tīng lái de shén me chǎng hé néng yòng xīn lǐ dà gài yǒu shù',
        'The ones I\'ll actually risk are just these few, all picked up from colleagues talking, so I have a decent sense of where they fit.',
      ],
      [
        '这个项目刚接手的时候千头万绪，现在总算走上正轨了。',
        'zhè ge xiàng mù gāng jiē shǒu de shí hou qiān tóu wàn xù xiàn zài zǒng suàn zǒu shàng zhèng guǐ le',
        'When I took this project over it was a complete tangle; it\'s finally on track.',
        '千头万绪 is the usable kind of 成语 — it reports a situation instead of delivering a lesson, and a four-character idiom with a moral in it turns a status update into a speech. 走上正轨 is not a 成语 at all, just an everyday collocation that happens to run four characters, which is exactly why nobody hears it as a flourish.',
      ],
      [
        '所以我宁可少用，也不硬往里塞，说到点子上就行了。',
        'suǒ yǐ wǒ nìng kě shǎo yòng yě bú yìng wǎng lǐ sāi shuō dào diǎn zi shàng jiù xíng le',
        'So I\'d rather use fewer than shoehorn one in — if it makes the point, that\'s enough.',
      ],
    ],
  },
]
